import { useState } from 'react';
import {
  ArrowRightOnRectangleIcon,
  PencilIcon,
  PlusIcon,
  ShareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useCollections } from './Contexts/CollectionsContext';
import { useMyResourcesModals } from './Contexts/MyResourcesModalsContext';
import { publishFolder } from '../../services/UserPacketCollectionService';
import { DELETE_TYPES } from './Modals/DeleteConfirmationModal';
import { DropdownButton } from './PacketCard';
import { CollectionSidebarRow } from './CollectionSidebarRow';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import { Mixpanel } from '../../services/Mixpanel';

export const SharedFoldersList = ({ onCollectionDeleted }) => {
  const {
    setShowCreateOrRenameCollectionModal,
    setShowDeleteConfirmationModal,
    setShareCollectionModalOpen,
  } = useMyResourcesModals();
  const { collections, deleteCollection, leaveCollection } = useCollections();
  // The member folder pending a "leave" confirmation, or null.
  const [leavingCollection, setLeavingCollection] = useState(null);

  // Owned folders get the full owner menu; folders you're only a member of show a byline and a
  // single "Leave folder" action (owner-only actions are hidden). Default missing flag to owned.
  const ownedCollections = collections?.filter((c) => c.is_owner !== false) || [];
  const memberCollections = collections?.filter((c) => c.is_owner === false) || [];

  const ownerMenuButtons = (collection) => [
    <DropdownButton
      key='share'
      label='Share'
      aria-label='Share folder'
      icon={<ShareIcon className='w-4 h-4' />}
      onClick={async () => {
        Mixpanel.track('Share Collection Clicked', {
          collection_id: collection.id,
          collection_title: collection.title,
        });
        // Publish first so a copied /c/:id link resolves even before anyone is invited. Await it
        // so a failed publish doesn't silently leave a 404ing link; on failure still open the
        // modal (the invite path re-publishes) but surface it.
        try {
          await publishFolder(collection.id);
        } catch {
          Mixpanel.track('Share Folder Publish Failed', { collection_id: collection.id });
        }
        setShareCollectionModalOpen({ collection });
      }}
    />,
    <DropdownButton
      key='rename'
      label='Rename'
      aria-label='Rename folder'
      icon={<PencilIcon className='w-4 h-4' />}
      onClick={() => {
        Mixpanel.track('Rename Collection Clicked', {
          collection_id: collection.id,
          collection_title: collection.title,
        });
        setShowCreateOrRenameCollectionModal({ collection });
      }}
    />,
    <DropdownButton
      key='delete'
      label='Delete'
      aria-label='Delete folder'
      icon={<TrashIcon className='w-4 h-4' />}
      onClick={() => {
        Mixpanel.track('Delete Collection Clicked', {
          collection_id: collection.id,
          collection_title: collection.title,
        });
        setShowDeleteConfirmationModal({
          type: DELETE_TYPES.COLLECTION,
          item: collection,
          onDelete: async () => {
            await deleteCollection(collection.id);
            onCollectionDeleted?.(collection.id);
          },
        });
      }}
    />,
  ];

  return (
    <div>
      <div className='px-2 flex flex-row justify-between items-center mb-2'>
        <span className={` text-gray-900`}>Folders</span>
        <button
          data-tutorial-id='bts-create-folder'
          aria-label='Create folder'
          className={`hover:bg-gray-200 rounded-md p-1 `}
          onClick={() => {
            Mixpanel.track('Add Collection Clicked', {
              location: 'my_resources',
            });
            setShowCreateOrRenameCollectionModal({ collection: null });
          }}>
          <PlusIcon className='w-4 h-4 text-gray-900  ' />
        </button>
      </div>
      <div className='flex flex-col gap-y-1'>
        {ownedCollections.map((collection) => (
          <CollectionSidebarRow
            key={collection.id}
            collection={collection}
            location='my_resources_collections_list'
            menuButtons={ownerMenuButtons(collection)}
          />
        ))}
      </div>

      {memberCollections.length > 0 && (
        <>
          <div className='px-2 mt-4 mb-2'>
            <span className='text-gray-900'>Shared with me</span>
          </div>
          <div className='flex flex-col gap-y-1'>
            {memberCollections.map((collection) => (
              <CollectionSidebarRow
                key={collection.id}
                collection={collection}
                ownerName={collection.owner_name}
                location='my_resources_shared_collections_list'
                menuButtons={[
                  <DropdownButton
                    key='leave'
                    label='Leave folder'
                    aria-label='Leave folder'
                    icon={<ArrowRightOnRectangleIcon className='w-4 h-4' />}
                    onClick={() => setLeavingCollection(collection)}
                  />,
                ]}
              />
            ))}
          </div>
        </>
      )}

      <ConfirmationDialog
        open={!!leavingCollection}
        type='warning'
        icon={<ArrowRightOnRectangleIcon className='w-5 h-5 text-red-600' />}
        title='Leave this folder?'
        message={
          leavingCollection
            ? `You'll lose access to "${leavingCollection.title}" until you're invited again. Resources you added will stay in the folder.`
            : ''
        }
        confirmText='Leave folder'
        cancelText='Cancel'
        handleCancel={() => setLeavingCollection(null)}
        handleConfirm={async () => {
          Mixpanel.track('Collection Left', {
            collection_id: leavingCollection.id,
            collection_title: leavingCollection.title,
          });
          await leaveCollection(leavingCollection.id);
          setLeavingCollection(null);
        }}
      />
    </div>
  );
};
